import React, { useState, useContext, useEffect } from "react";
import DataTable from 'core/ui/data-table';
import FormatDateTime from "core/utils/FormatDateTime";
import FormatCurrency from 'core/utils/FormatCurrency';
import FormatNumber from 'core/utils/FormatNumber';
import SessionContext from 'SessionContext';
import { withRouter, Link } from 'react-router-dom';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import ConfirmSuccess from "../core/ui/confirm-success";
import useAudienceStore from "../store/useAudienceStore";
import { formatPopulationDomain } from "./explorer";

const Orders = (props) => {
  const context = useContext(SessionContext);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [newCompanyId, setNewCompanyId] = useState(null);
  const [userCancelled, setUserCancelled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [chartsLoaded, setChartsLoaded] = useState(context.chartsStore.loaded);
  const [pendingOrderNavigation, setPendingOrderNavigation] = useState(null);
  const [pendingOrderNavigationUrl, setPendingOrderNavigationUrl] = useState("");
  const [searchParams, setSearchParams] = useState(() => {
    const queryParams = new URLSearchParams(props.location.search);
    const _search = {};
    for (let [key, value] of queryParams.entries()) { _search[key] = value; }
    return _search;
  });

  useEffect(() => {
    if (chartsLoaded !== context.chartsStore.loaded) {
      setChartsLoaded(context.chartsStore.loaded);
    }

    if (chartsLoaded && pendingOrderNavigation) {
      props.history.push(`${pendingOrderNavigationUrl}${pendingOrderNavigation.id}`);
      setPendingOrderNavigation(null);
      setPendingOrderNavigationUrl("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[chartsLoaded, pendingOrderNavigation, context.chartsStore.loaded]);

  useEffect(() => {
    setSearchParams(getSearchQueryFromUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.location.search]);

  function onClickOrderStatus(value) {
    const orderId = {id: value.id};
    return (<div className="status-link">
      <i className="fa fa-circle-info i-info" />{" "}
      <Link to={{pathname:"/order-status", state: orderId}}>
        {value.status}
      </Link>
      </div>);
  }

  function checkIfPopulationMatches(order, url) {
    context.crossfilterStore.restoreFromAudience("clear_all");
    if (order.population_domain !== context.crossfilterStore.explorerModule) {
      context.crossfilterStore.setExplorerModule(order.population_domain);
    }

    if (chartsLoaded) {
      // Already loaded — navigate immediately
      props.history.push(`${url}${order.id}`);
    } else {
      // Not yet loaded — store the order and wait
      setPendingOrderNavigation(order);
      setPendingOrderNavigationUrl(url);
    }
  }

  function onClickOrderEdit(order) {
    // navigates to order edit page
    checkIfPopulationMatches(order, "/order#order=");
  }

  function onClickOrderDetails(order) {
    // navigates to order details page
    checkIfPopulationMatches(order, "/order-details#order=");
  }

  function onChangeCompany(param_id=null) {
    if(context.appState.loaded) {
      let _id = param_id || newCompanyId;
      const company = context.appState.identity.assigned_companies.find(c => c.id === _id);
      if(company) {
        localStorage.setItem("am:audience:reloadApp", Math.random());
        context.notificationsStore.add("Switched to new Company", company.name);
        context.crossfilterStore.restoreFromAudience({});
        context.appState.updateActiveCompany(company);
      } else {
        console.log("Company not found");
        setShowUnsavedModal(false);
        setUserCancelled(true);
        setIsLoading(false);
      }
      
    }
  }

  function onCancelChangeCompany() {
    setShowUnsavedModal(false);
    setUserCancelled(true);
    setTimeout(() => {
      getSearchQueryFromUrl();
    }, 500);
  }

  function getSearchQueryFromUrl() {
    const queryParams = new URLSearchParams(props.location.search);
    const _search = {};
    let newCompanyIdFromUrl = null;
    let current_company_id = context.appState.activeCompany.id;

    const { isAudienceSaved } = useAudienceStore.getState();

    for (let [key, value] of queryParams.entries()) {
      _search[key] = value;
      if(key === 'company_id') {
        newCompanyIdFromUrl = value;
      }
    }

    if (!userCancelled && newCompanyIdFromUrl && newCompanyIdFromUrl.toString() !== current_company_id.toString()) {
      if (isAudienceSaved) {
        if (Number(newCompanyIdFromUrl) !== newCompanyId) {
          onChangeCompany(Number(newCompanyIdFromUrl));
        }
      } else {
        if (!showUnsavedModal) {
          setShowUnsavedModal(true);
          setIsLoading(true);
          setNewCompanyId(Number(newCompanyIdFromUrl));
        }
      }
    } else if (isLoading) {
      setIsLoading(false);
    }

    return _search;
  }

const access = context.appState.userCan;

let table_columns = [
  {header: 'Order Number', key: 'id'},
  {header: 'Order Name', key: 'name'},
  {header: 'Create Date', key: 'create_date', render: (value) => <FormatDateTime date={value.created_date} />},
  {header: 'Order Date', key: 'order_date', render: (value) => <FormatDateTime date={value.order_date} />},
  {header: 'Created By', key: 'created_by'},
  {header: 'Status', key: 'status', render: (value) => onClickOrderStatus(value) },
  {header: 'Population Domain', key: 'population_domain', render: (value) => formatPopulationDomain(value.population_domain) },
  {header: 'Quantity', key: 'quantity', render: (value) => <FormatNumber value={value.quantity} />},
  {header: 'Cost', key: 'cost', render: (value) => <FormatCurrency value={value.total_cost} />}
];

if(access('administration')) {
  table_columns.splice(5, 0, { // inserts before "Status"
    header: 'Do Not Invoice',
    key: 'do_not_invoice',
    render: (value) => {
      return value.do_not_invoice ? "Do Not Invoice" : "Invoice";
    }
  });
}

const table_actions = (order) =>
<OrderActions order={order}
  continueOrder={() => onClickOrderEdit(order)}
  orderDetails={() => onClickOrderDetails(order)}
  access={access}
/>

return (
    <div className="page orders-page">
      <div className="sticky-page-header">
        <h1>Orders</h1>
      </div>
      { !isLoading ? (
        <DataTable 
          query='order/list'
          cols={table_columns}
          actions={table_actions}
          empty='No Orders Found'
          params={searchParams}
        />
      ) : "Loading..." }
      <SwitchCompanyModal 
        show={showUnsavedModal} 
        onCancel={onCancelChangeCompany}
        onContinue={() => onChangeCompany()}
      />
    </div>
);
};
export default withRouter(Orders);

function OrderActions({order, continueOrder, orderDetails, access}) {
  const session = useContext(SessionContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const toggleDeleteModal = () => {
    setShowDeleteModal(v => !v);
  }

  const onFinalDeleteOrder = () => {
    session.command(`order/delete?id=${order.id}`)
      .then(() => {
        toggleDeleteModal();
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          window.location.reload();
        }, 2000);
      })
      .catch((err) => {
        console.log("Delete Failed", JSON.stringify(err));
      })
  }

  let actions = "";
  if ((order.status === "Created" || order.status === "Mapping") && access('order_edit')) {
    actions =
      <ul className="action-menu">
        <li>
          <Button color="link" size="sm" onClick={() => continueOrder(order)}>
            <i className="bi bi-pencil" /> Edit
          </Button>
          {access('order_delete') && (
            <Button color="link" size="sm" onClick={toggleDeleteModal}>
              <i className="bi bi-trash" /> Delete
            </Button>
          )}
        </li>
      </ul>
  } else if(order.status === "Pending Approval") {
    actions =
      <ul className="action-menu">
        <li>
          <Button color="link" size="sm" onClick={() => continueOrder(order)}>
            <i className="bi bi-pencil" /> Review Approval
          </Button>
          {access('order_delete') && (
            <Button color="link" size="sm" onClick={toggleDeleteModal}>
              <i className="bi bi-trash" /> Delete
            </Button>
          )}
        </li>
      </ul>
  } else if (access('order_view')) {
    actions =
    <ul id="action-menu" className="action-menu">
      <li>
        <Button color="link" size="sm" onClick={() => orderDetails(order)}>
          <i className="bi bi-eye" /> Details
        </Button>
        {access('order_delete') && (
          <Button color="link" size="sm" onClick={toggleDeleteModal}>
            <i className="bi bi-trash" /> Delete
          </Button>
        )}
      </li>
    </ul>
  }

  return (<>
    {actions}
    <Modal centered backdrop
      isOpen={showDeleteModal}
    >
      <ModalHeader>
        Delete Order
      </ModalHeader>
      <ModalBody>
        <Alert color="light" className="d-flex">
          <i className="bi bi-exclamation-octagon"></i>
          <div>
          Selecting <b>delete</b> will permanently remove all instances from AudienceMaker and cannot be undone. Are you sure you want to continue?
          </div>
        </Alert>
        <Alert color="light" className="">
          <p>
            <span className="header-modal-text">Order Name</span>
            <span className="d-block ms-3">{order.name}</span>
          </p>
          <p>
            <span className="header-modal-text">Description</span>
            <span className="d-block ms-3">{order.description}</span>
          </p>
        </Alert>
      </ModalBody>
      <ModalFooter>
        <Button className="promote" onClick={toggleDeleteModal}>Cancel</Button>
        <Button onClick={onFinalDeleteOrder}>Delete</Button>
      </ModalFooter>
    </Modal>
    <ConfirmSuccess show={showSuccess} label="Order deleted successfully." />
  </>);
}

/*
 * modal to warn the user that they have unsaved changes in the audience
 */
const SwitchCompanyModal = ({show, onCancel, onContinue}) => {
  return (
    <Modal size="lg" centered isOpen={show} backdrop>
      <ModalHeader>Unsaved Audience</ModalHeader>
      <ModalBody>
        <h2>Your audience changes will be lost!</h2>
        <p>
          Before continuing, consider saving your audience changes from the
          explorer page with the update button first.
        </p>
        <p>Click "<strong>Cancel</strong>" to see the orders in the current active company.</p>
        <p>Click "<strong>Continue</strong>" to switch companies and see the orders (Audience will be discarded)</p>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel}>Cancel</Button>
        <Button className={" promote"} onClick={onContinue}>
          Continue
        </Button>
      </ModalFooter>
    </Modal>
  );
};
